import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import type { GroveDecoration, GrovePlacement } from '../lib/types';
import {
  idleVariant,
  makeCritter,
  pairInteractions,
  paintCritters,
  stepCritters,
  type Critter,
} from './groveLife';

/** Decorations that read as "big scenery" get the large box. */
const DECOR_SIZE: Record<string, 'sm' | 'md' | 'lg'> = {
  oak: 'lg', pine: 'lg', shrine: 'lg', fountain: 'lg',
  pond: 'md', bench: 'md', campfire: 'md', fence: 'md', bush: 'md', log: 'md', signpost: 'md',
  rock: 'sm', flowers: 'sm', grass: 'sm', mushroom: 'sm', lantern: 'sm',
};

/**
 * Depth: things lower down the scene are nearer the viewer, so z-index follows
 * y. Without this a Pokemon standing "in front of" a tree would be drawn behind
 * it and the illusion collapses.
 */
const depthZ = (y: number, layer = 0) => Math.round(y * 10) + layer * 1000;

/** Matches the server's clamp, so a wandering Pokemon can never stand somewhere a saved one could not. */
const BOUNDS = { minX: 4, maxX: 96, minY: 46, maxY: 96 };

export interface DragState {
  kind: 'pokemon' | 'decoration';
  id: string;
  x: number;
  y: number;
}

interface Props {
  backgroundKey: string;
  groundStyle: string;
  weatherKey?: string;
  pokemon: GrovePlacement[];
  decorations: GroveDecoration[];
  editing: boolean;
  selectedId: string | null;
  /** Pokemon the current viewer has already fed (cooldown). */
  fedIds: Set<string>;
  canFeed: boolean;
  onSelect: (placement: GrovePlacement, element: HTMLElement) => void;
  /** Mouse-only. Opens the panel without pinning it, so feeding is one click. */
  onHover?: (placement: GrovePlacement, element: HTMLElement) => void;
  onHoverEnd?: () => void;
  onSelectDecoration?: (decoration: GroveDecoration) => void;
  onMove: (state: DragState) => void;
  floats: Array<{ id: number; x: number; y: number; text: string; big: boolean }>;
}

export function GroveScene({
  backgroundKey,
  groundStyle,
  weatherKey = 'clear',
  pokemon,
  decorations,
  editing,
  selectedId,
  fedIds,
  canFeed,
  onSelect,
  onHover,
  onHoverEnd,
  onSelectDecoration,
  onMove,
  floats,
}: Props) {
  const sceneRef = useRef<HTMLDivElement>(null);
  const [dragging, setDragging] = useState<string | null>(null);

  // A pointer that moves less than this is a tap, not a drag — without it,
  // trying to click a Pokemon on a phone almost always nudges it instead.
  const DRAG_THRESHOLD = 6;
  const gesture = useRef<{ startX: number; startY: number; moved: boolean } | null>(null);

  /* --- Idle life --------------------------------------------------------- */

  const crittersRef = useRef<Critter[]>([]);
  const frameRef = useRef<number | null>(null);
  /**
   * id -> sprite node. Keyed by id rather than by array index: ref callbacks
   * fire before effects, so an index would be resolved against a roster that
   * has not been rebuilt yet and every sprite would come back null on mount.
   */
  const elementsRef = useRef<Map<string, HTMLElement>>(new Map());

  /**
   * Life is off while editing (positions must be exactly what you dragged them
   * to), and off for anyone who has asked their system for reduced motion. The
   * scene still renders — it just holds still.
   */
  const [reducedMotion, setReducedMotion] = useState(false);
  useEffect(() => {
    const query = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReducedMotion(query.matches);
    const onChange = (event: MediaQueryListEvent) => setReducedMotion(event.matches);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);

  const alive = !editing && !reducedMotion;

  // Rebuild the roster when the cast changes, carrying over anyone already on
  // stage so switching Groves does not teleport the ones that stayed.
  useEffect(() => {
    const existing = new Map(crittersRef.current.map((critter) => [critter.id, critter]));
    crittersRef.current = pokemon.map((placement) => {
      const previous = existing.get(placement.pokemon.id);
      if (previous) {
        // The player may have dragged it: home always follows the saved spot.
        previous.homeX = placement.x;
        previous.homeY = placement.y;
        return previous;
      }
      return makeCritter(placement.pokemon.id, placement.x, placement.y, placement.flipped);
    });
  }, [pokemon]);

  // Editing snaps everything back to its saved spot, so what you drag is what
  // you see and what you save.
  useEffect(() => {
    if (alive) return;
    for (const critter of crittersRef.current) {
      critter.x = critter.homeX;
      critter.y = critter.homeY;
      critter.behaviour = 'idle';
    }
    paintCritters(crittersRef.current, elementsRef.current);
  }, [alive, pokemon]);

  useEffect(() => {
    if (!alive || crittersRef.current.length === 0) return;

    let last = performance.now();
    let sinceInteractionCheck = 0;
    let running = true;

    const tick = (now: number) => {
      if (!running) return;
      // Clamped: a backgrounded tab hands back a delta of many seconds, which
      // would teleport everything the moment you return to it.
      const delta = Math.min((now - last) / 1000, 0.1);
      last = now;

      stepCritters(crittersRef.current, delta, now, BOUNDS);

      // The neighbour check is O(n²); at four times a second nobody can tell
      // it is not running every frame.
      sinceInteractionCheck += delta;
      if (sinceInteractionCheck > 0.25) {
        sinceInteractionCheck = 0;
        pairInteractions(crittersRef.current, now);
      }

      paintCritters(crittersRef.current, elementsRef.current);
      frameRef.current = requestAnimationFrame(tick);
    };

    frameRef.current = requestAnimationFrame(tick);

    // A hidden tab should not be simulating a Grove nobody is looking at.
    const onVisibility = () => {
      if (document.hidden) {
        if (frameRef.current !== null) cancelAnimationFrame(frameRef.current);
        frameRef.current = null;
      } else if (frameRef.current === null && running) {
        last = performance.now();
        frameRef.current = requestAnimationFrame(tick);
      }
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      running = false;
      if (frameRef.current !== null) cancelAnimationFrame(frameRef.current);
      frameRef.current = null;
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [alive, pokemon]);

  /* --- Dragging ---------------------------------------------------------- */

  const toScenePercent = useCallback((clientX: number, clientY: number) => {
    const rect = sceneRef.current?.getBoundingClientRect();
    if (!rect) return { x: 50, y: 50 };
    return {
      x: Math.max(3, Math.min(97, ((clientX - rect.left) / rect.width) * 100)),
      y: Math.max(6, Math.min(94, ((clientY - rect.top) / rect.height) * 100)),
    };
  }, []);

  function beginDrag(
    event: ReactPointerEvent<HTMLElement>,
    kind: 'pokemon' | 'decoration',
    id: string,
  ) {
    if (!editing) return;
    event.stopPropagation();
    gesture.current = { startX: event.clientX, startY: event.clientY, moved: false };
    setDragging(id);
    event.currentTarget.setPointerCapture(event.pointerId);

    const onPointerMove = (moveEvent: PointerEvent) => {
      const state = gesture.current;
      if (!state) return;
      const dx = Math.abs(moveEvent.clientX - state.startX);
      const dy = Math.abs(moveEvent.clientY - state.startY);
      if (!state.moved && dx + dy < DRAG_THRESHOLD) return;
      state.moved = true;
      const { x, y } = toScenePercent(moveEvent.clientX, moveEvent.clientY);
      onMove({ kind, id, x, y });
    };

    const onPointerUp = () => {
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
      setDragging(null);
      gesture.current = null;
    };

    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
  }

  return (
    <div
      ref={sceneRef}
      className={`pxframe grove grove-bg--${backgroundKey} grove-ground--${groundStyle} grove-weather--${weatherKey} ${
        editing ? 'grove--editing' : ''
      } ${dragging ? 'is-dragging' : ''}`}
    >
      <div className="grove__sky" />
      <div className="grove__horizon" />
      <div className={`grove__ground grove-ground--${groundStyle}`} />
      {weatherKey !== 'clear' && <div className={`grove__weather grove__weather--${weatherKey}`} aria-hidden />}

      {decorations.map((decoration) => (
        <div
          key={decoration.id}
          className={`grove-item decor decor--${DECOR_SIZE[decoration.art] ?? 'md'} ${
            dragging === decoration.id ? 'grove-item--dragging' : ''
          }`}
          style={{
            left: `${decoration.x}%`,
            top: `${decoration.y}%`,
            zIndex: depthZ(decoration.y, decoration.layer),
            transform: `translate(-50%, -100%) scale(${decoration.scale})${decoration.flipped ? ' scaleX(-1)' : ''}`,
            cursor: editing ? 'grab' : 'default',
          }}
          onPointerDown={(event) => beginDrag(event, 'decoration', decoration.id)}
          onClick={() => editing && onSelectDecoration?.(decoration)}
          role={editing ? 'button' : undefined}
          aria-label={editing ? `Move ${decoration.name}` : undefined}
        >
          <span className={`decor__art art-${decoration.art}`} aria-hidden />
          <span className="grove-item__shadow" />
        </div>
      ))}

      {pokemon.map((placement) => {
        const fed = fedIds.has(placement.pokemon.id);
        return (
          <div
            key={placement.placementId}
            /* The behaviour engine writes left/top/zIndex straight onto this
               node every frame. React sets the initial position and then stays
               out of the way — thirty-six sprites reconciling at 60fps is not
               something a browser should be asked to do. */
            ref={(node) => {
              if (node) elementsRef.current.set(placement.pokemon.id, node);
              else elementsRef.current.delete(placement.pokemon.id);
            }}
            className={`grove-item grove-critter ${
              dragging === placement.pokemon.id ? 'grove-item--dragging' : ''
            }`}
            data-behaviour="idle"
            data-facing={placement.flipped ? 'left' : 'right'}
            style={{
              left: `${placement.x}%`,
              top: `${placement.y}%`,
              zIndex: depthZ(placement.y, placement.layer),
            }}
          >
            <button
              type="button"
              className={`grove-mon ${selectedId === placement.pokemon.id ? 'grove-mon--selected' : ''}`}
              onPointerDown={(event) => beginDrag(event, 'pokemon', placement.pokemon.id)}
              onClick={(event) => {
                // Suppress the click that ends a drag.
                if (gesture.current?.moved) return;
                onSelect(placement, event.currentTarget);
              }}
              /* Hover opens the panel so that feeding is a single click. Guarded
                 to real mice: a touch also fires pointerenter, and on a phone
                 that would pop the panel under the finger before the tap even
                 registers. Edit mode is excluded because there the gesture is a
                 drag, not an inspection. */
              onPointerEnter={(event) => {
                if (event.pointerType !== 'mouse' || editing) return;
                onHover?.(placement, event.currentTarget);
              }}
              onPointerLeave={(event) => {
                if (event.pointerType !== 'mouse' || editing) return;
                onHoverEnd?.();
              }}
              aria-label={`${placement.pokemon.displayName}, Level ${placement.pokemon.level}`}
            >
              <span className="grove-mon__tag">
                {placement.pokemon.displayName} · Lv {placement.pokemon.level}
              </span>
              <img
                className={`grove-mon__sprite grove-idle-${idleVariant(placement.pokemon.id)}`}
                src={placement.pokemon.sprite}
                alt=""
                draggable={false}
              />
              {placement.pokemon.onTeam && (
                <span className="grove-mon__star" title="On your Team" aria-hidden>
                  ★
                </span>
              )}
              {canFeed && !editing && (
                <span className={`grove-mon__pip ${fed ? 'grove-mon__pip--done' : ''}`} aria-hidden />
              )}
            </button>
            <span className="grove-item__shadow" />
          </div>
        );
      })}

      {floats.map((float) => (
        <span
          key={float.id}
          className={`grove-float ${float.big ? 'grove-float--level' : ''}`}
          style={{ left: `${float.x}%`, top: `${float.y}%` }}
        >
          {float.text}
        </span>
      ))}
    </div>
  );
}
